/**
 * Helpers for turning Mira AI evidence (reasoning) uploaded-document citations into links that work
 * in a static exported PDF/DOCX.
 *
 * Reasoning references uploaded documents via `<evidence-file-button href="/settings/ai/files/…/download-url">`.
 * That href is a RELATIVE, authenticated API path — opening it directly 401s and it can't resolve in a
 * static document. So at export time the Portal resolves each path to its short-lived **signed**
 * download URL (openable without auth) and swaps it into the href; the pdf-generator's `section-evidence`
 * then renders the tag as a real `<a>` because the href is now absolute. External `<evidence-button>`
 * links are already absolute and are untouched.
 *
 * The regex-based extract/replace are pure; the async signed-URL fetch is orchestrated by the caller
 * (the export helper, which has the authenticated HTTP client).
 */
const FILE_BUTTON_HREF_SOURCE = '(<evidence-file-button\\s+href=")([^"]*)(")';

/** Unique relative (starts-with-`/`) uploaded-document hrefs referenced in the reasoning HTML. */
export function extractEvidenceFileHrefs(html: string | null | undefined): string[] {
    if (!html) {
        return [];
    }
    const hrefs = new Set<string>();
    const regex = new RegExp(FILE_BUTTON_HREF_SOURCE, 'gi');
    let match: RegExpExecArray | null;
    while ((match = regex.exec(html)) !== null) {
        const href = match[2];
        if (href && href.startsWith('/')) {
            hrefs.add(href);
        }
    }
    return Array.from(hrefs);
}

/**
 * Swaps each uploaded-document href for its resolved signed URL. Hrefs without a resolved entry are
 * left as-is (the pdf-generator then renders them as plain text — never a broken/401 link). A function
 * replacer is used so `$`/`&` sequences in signed URLs are inserted literally.
 */
export function replaceEvidenceFileHrefs(html: string | null | undefined, resolved: Map<string, string>): string | null {
    if (!html) {
        return html ?? null;
    }
    const regex = new RegExp(FILE_BUTTON_HREF_SOURCE, 'gi');
    return html.replace(regex, (match, prefix: string, href: string, suffix: string) => {
        const signedUrl = resolved.get(href);
        return signedUrl ? `${prefix}${signedUrl}${suffix}` : match;
    });
}
